import type { FC, FormEvent, ReactNode } from 'react'
import type { FieldErrors, TeamEntryField, TeamEntryResponse, TeamEntryStatus } from '@/lib/entries'

import { useState } from 'react'
import { CircleCheck, LoaderCircle } from 'lucide-react'
import { buttonBase, buttonVariants } from '@/components/landing/button-link'
import { cn } from '@/lib/utils'
import { readTeamEntry, validateTeamEntry } from '@/lib/entries'

import {
    Field,
    HoneypotField,
    SelectShell,
    inputClasses,
    selectClasses
} from '@/components/landing/form-field'

import { DIVISIONS, EVENT } from '@/constants/event'

interface TeamEntryFormProps {
    division: string
    onDivisionChange: (division: string) => void
    onEntered: () => void
}

type FormState =
    | { kind: 'idle' }
    | { kind: 'sending' }
    | { kind: 'done', status: TeamEntryStatus, captain: string, division: string }
    | { kind: 'failed', message: string }

const RESULT_COPY: Record<TeamEntryStatus, { title: string, body: string }> = {
    entered: {
        title: "You're on the entry list.",
        body: 'The tournament desk will email you to confirm your spot and collect the entry fee.'
    },
    waitlist: {
        title: "You're on the waitlist.",
        body: 'This draw is full right now. If a team drops out, the desk emails the next team on the list.'
    },
    duplicate: {
        title: 'Already entered.',
        body: 'This email already has a team in this division, so there is nothing more to do. The desk will be in touch.'
    }
}

export const TeamEntryForm: FC<TeamEntryFormProps> = ({ division, onDivisionChange, onEntered }): ReactNode => {
    const [state, setState] = useState<FormState>({ kind: 'idle' })
    const [errors, setErrors] = useState<FieldErrors<TeamEntryField>>({})

    const selected = DIVISIONS.find((item) => item.id === division) ?? DIVISIONS[0]

    const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()

        const form = event.currentTarget
        const values = Object.fromEntries(new FormData(form).entries()) as Record<string, unknown>
        const input = readTeamEntry(values)
        const localErrors = validateTeamEntry(input)

        setErrors(localErrors)

        if (Object.keys(localErrors).length) {
            const firstField = Object.keys(localErrors)[0]
            form.querySelector<HTMLElement>(`[name="${firstField}"]`)?.focus()
            return
        }

        setState({ kind: 'sending' })

        try {
            const response = await fetch('/api/team-entry', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(values)
            })

            const result = (await response.json().catch(() => null)) as TeamEntryResponse | null

            if (result?.ok) {
                setState({ kind: 'done', status: result.status, captain: input.captainName.split(' ')[0], division: selected.name })
                form.reset()
                onEntered()
                return
            }

            setErrors(result?.fieldErrors ?? {})
            setState({ kind: 'failed', message: result?.message ?? 'Something went wrong. Please try again.' })
        }
        catch {
            setState({ kind: 'failed', message: 'We could not reach the entry desk. Check your connection and try again.' })
        }
    }

    if (state.kind === 'done') {
        const copy = RESULT_COPY[state.status]

        return (
            <div role='status' className='flex min-h-[28rem] flex-col items-start justify-center gap-4'>
                <CircleCheck aria-hidden='true' className='size-10 text-lime' />

                <p className='font-mono text-[11px] uppercase tracking-[0.2em] text-cream/60'>
                    {state.division}
                </p>

                <h3 className='font-heading text-4xl font-extrabold uppercase leading-[0.92] md:text-5xl'>
                    {copy.title}
                </h3>

                <p className='max-w-sm text-base leading-relaxed text-cream/80'>
                    {state.captain ? `Thanks, ${state.captain}. ` : ''}
                    {copy.body}
                </p>

                <button
                    type='button'
                    onClick={() => setState({ kind: 'idle' })}
                    className={cn(buttonBase, buttonVariants['outline-cream'], 'mt-2')}
                >
                    Enter another team
                </button>
            </div>
        )
    }

    const sending = state.kind === 'sending'

    return (
        <form noValidate onSubmit={submit} className='relative flex flex-col gap-5'>
            <HoneypotField />

            <Field id='team-division' label='Division' error={errors.division}>
                <SelectShell>
                    <select
                        id='team-division'
                        name='division'
                        value={division}
                        onChange={(event) => onDivisionChange(event.target.value)}
                        aria-invalid={Boolean(errors.division)}
                        className={selectClasses}
                    >
                        {DIVISIONS.map((item) => (
                            <option key={item.id} value={item.id}>
                                {item.name}
                            </option>
                        ))}
                    </select>
                </SelectShell>
            </Field>

            <div className='grid gap-5 sm:grid-cols-2'>
                <Field id='team-captain-name' label='Your full name' error={errors.captainName}>
                    <input
                        id='team-captain-name'
                        name='captainName'
                        type='text'
                        autoComplete='name'
                        required
                        aria-invalid={Boolean(errors.captainName)}
                        aria-describedby={errors.captainName ? 'team-captain-name-error' : undefined}
                        className={inputClasses}
                    />
                </Field>

                <Field id='team-partner-name' label="Partner's full name" error={errors.partnerName}>
                    <input
                        id='team-partner-name'
                        name='partnerName'
                        type='text'
                        autoComplete='off'
                        required
                        aria-invalid={Boolean(errors.partnerName)}
                        aria-describedby={errors.partnerName ? 'team-partner-name-error' : undefined}
                        className={inputClasses}
                    />
                </Field>
            </div>

            <Field id='team-captain-email' label='Your email' error={errors.captainEmail}>
                <input
                    id='team-captain-email'
                    name='captainEmail'
                    type='email'
                    autoComplete='email'
                    inputMode='email'
                    required
                    placeholder='you@example.com'
                    aria-invalid={Boolean(errors.captainEmail)}
                    aria-describedby={errors.captainEmail ? 'team-captain-email-error' : undefined}
                    className={inputClasses}
                />
            </Field>

            <Field
                id='team-rating'
                label='Team DUPR rating'
                optional
                hint='Average of both players, for seeding.'
                error={errors.teamRating}
            >
                <input
                    id='team-rating'
                    name='teamRating'
                    type='text'
                    inputMode='decimal'
                    placeholder='4.25'
                    autoComplete='off'
                    aria-invalid={Boolean(errors.teamRating)}
                    aria-describedby={errors.teamRating ? 'team-rating-error' : 'team-rating-hint'}
                    className={inputClasses}
                />
            </Field>

            <div className='flex flex-col gap-3 border-t border-cream/15 pt-5 sm:flex-row sm:items-center sm:justify-between'>
                <p className='text-sm text-cream/70'>
                    <span className='font-heading text-2xl font-black text-lime'>{`$${selected.fee}`}</span>
                    {' per team, paid after the desk confirms your spot'}
                </p>

                <button
                    type='submit'
                    disabled={sending}
                    className={cn(buttonBase, buttonVariants.lime, 'shrink-0')}
                >
                    {sending && <LoaderCircle aria-hidden='true' className='size-4 animate-spin motion-reduce:animate-none' />}
                    {sending ? 'Sending entry' : 'Enter our team'}
                </button>
            </div>

            <p aria-live='polite' className={cn('text-sm', state.kind === 'failed' ? 'text-coral' : 'sr-only')}>
                {state.kind === 'failed' ? state.message : ''}
            </p>

            <p className='text-xs text-cream/50'>
                {`Entries close ${EVENT.entriesClose} or when a draw fills. We only use your email for this tournament.`}
            </p>
        </form>
    )
}
